import LazyLoadImages from "../events/ImageLoaded";
import LabModal from "../components/LabModal";

export default class ArchiveProjectLab {
    static init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.start());
        } else {
            this.start();
        }
    }

    static start() {
        LabModal.init();

        this.form = document.getElementById('lab-filters');
        this.container = document.getElementById('projects-container');
        this.searchInput = document.getElementById('lab-search');
        this.searchCross = document.querySelector('.search-input-cross');
        this.headerFilterBtn = document.querySelector('.menu-item-filter');

        if (!this.form || !this.container) return;

        this.resetButton = document.getElementById('reset-lab-filters');
        this.ajaxUrl = window.location.href.split('?')[0];

        this.page = 1;
        this.loading = false;
        this.finished = false;
        this.timeout = null;

        console.log('✅ ArchiveProjectLab: Initialisé.');
        this.initParallax();
        this.updateFilterCount();
        this.bindEvents();
        this.initInfiniteScroll();
    }

    static initParallax() {
        if (window.innerWidth <= 991) return;

        this.cacheColumnPositions();
        this.applyParallax();

        let ticking = false;
        window.addEventListener('scroll', () => {
            if (window.innerWidth <= 991) return;
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.applyParallax();
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true });
    }

    static cacheColumnPositions() {
        const columns = this.container.querySelectorAll('.lab-column');
        this.colCache = [];
        columns.forEach((col) => {
            const style    = window.getComputedStyle(col);
            const matrix   = new DOMMatrixReadOnly(style.transform);
            const currentY = isNaN(matrix.m42) ? 0 : matrix.m42;
            const rect     = col.getBoundingClientRect();
            this.colCache.push({
                el:     col,
                docTop: rect.top + window.scrollY - currentY,
                height: col.offsetHeight
            });
        });
    }

    static applyParallax() {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        if (window.scrollY < 0 || window.scrollY > maxScroll) return;

        if (!this.colCache || this.colCache.length === 0) {
            this.cacheColumnPositions();
        }

        const speeds = [0.08, -0.05, 0.08, -0.05];

        this.colCache.forEach((data, index) => {
            if (!data.el.isConnected) return;
            const speed = speeds[index] || 0.05;
            
            // Calcul basé sur la distance entre le haut de la colonne et le haut du viewport
            // Ce calcul est indépendant de la hauteur de la colonne, évitant les sauts visuels
            const relativeScroll = window.scrollY - data.docTop;
            const yPos = Math.max(-100, Math.min(100, relativeScroll * speed));
            
            data.el.style.transform = `translate3d(0, ${yPos}px, 0)`;
        });
    }

    static bindEvents() {
        if (this.form) {
            const filterButtons = this.form.querySelectorAll('.btn-filter');
            filterButtons.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    btn.classList.toggle('selected');
                    this.updateFilterCount();
                    this.resetAndLoad();
                });
            });
        }

        if (this.searchInput) {
            this.searchInput.addEventListener('input', () => {
                clearTimeout(this.timeout);
                this.toggleCrossVisibility();
                this.timeout = setTimeout(() => this.resetAndLoad(), 500);
            });
            this.toggleCrossVisibility();
        }

        if (this.searchCross && this.searchInput) {
            this.searchCross.addEventListener('click', () => {
                this.searchInput.value = '';
                this.toggleCrossVisibility();
                this.resetAndLoad();
                this.searchInput.focus();
            });
        }

        if (this.form) {
            this.form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.resetAndLoad();
            });
        }

        if (this.resetButton) {
            this.resetButton.addEventListener('click', (e) => {
                e.preventDefault();
                if (this.form) {
                    this.form.querySelectorAll('.btn-filter.selected').forEach(btn => btn.classList.remove('selected'));
                }
                this.updateFilterCount();
                this.resetAndLoad();
            });
        }

        window.addEventListener('popstate', () => window.location.reload());
    }

    static updateFilterCount() {
        const count = this.form ? this.form.querySelectorAll('.btn-filter.selected').length : 0;

        if (this.headerFilterBtn) {
            if (count > 0) {
                this.headerFilterBtn.classList.add('has-filters');
            } else {
                this.headerFilterBtn.classList.remove('has-filters');
            }

            let text = 'Filtres';
            if (count === 1) text = '1 filtre';
            else if (count > 1) text = `${count} filtres`;

            this.headerFilterBtn.innerHTML = `<span>${text}</span>`;
        }

        if (this.resetButton) {
            this.resetButton.hidden = count === 0;
        }
    }

    static toggleCrossVisibility() {
        if (this.searchCross && this.searchInput) {
            if (this.searchInput.value.trim() !== '') {
                this.searchCross.style.display = 'block';
                this.searchCross.style.opacity = '1';
                this.searchCross.style.cursor = 'pointer';
            } else {
                this.searchCross.style.display = 'none';
            }
        }
    }

    static initInfiniteScroll() {
        const sentinel = document.getElementById('infinite-scroll-sentinel');
        if (!sentinel) return;

        this.maxPages = parseInt(sentinel.dataset.maxPages || 1);

        this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !this.loading && !this.finished && this.page < this.maxPages) {
                this.loadMore();
            }
        }, {rootMargin: '200px'});

        this.observer.observe(sentinel);
    }

    static resetAndLoad() {
        this.page = 1;
        this.finished = false;
        this.container.style.opacity = '0.5';
        this.fetchProjects(true);
    }

    static loadMore() {
        this.fetchProjects(false);
    }

    static fetchProjects(reset = false) {
        if (this.loading || (!reset && this.finished)) return;
        
        this.loading = true;
        const loader = document.getElementById('infinite-scroll-loader');
        if (loader) loader.style.display = 'block';

        const formData = new FormData(this.form || undefined);

        if (this.form) {
            const activeFilters = this.form.querySelectorAll('.btn-filter.selected');
            activeFilters.forEach(btn => {
                const parentWrapper = btn.closest('[data-name]');
                if (parentWrapper) {
                    formData.append(parentWrapper.dataset.name, btn.dataset.value);
                }
            });
        }

        if (this.searchInput && this.searchInput.value.trim() !== '') {
            formData.set('search', this.searchInput.value.trim());
        } else {
            formData.delete('search');
        }

        formData.append('ajax', '1');
        const nextPage = reset ? 1 : this.page + 1;
        formData.append('paged', nextPage);

        if (!reset) {
            const currentGrid = this.container.querySelector('#lab-grid');
            if (currentGrid) {
                const colHeights = [0, 0, 0, 0];
                currentGrid.querySelectorAll('.lab-column').forEach(col => {
                    const colIdx = parseInt(col.dataset.colIndex);
                    if (colIdx >= 0 && colIdx < 4) {
                        col.querySelectorAll('.project_lab-card').forEach(card => {
                            try {
                                const data = JSON.parse(card.dataset.projectJson);
                                colHeights[colIdx] += (data.image_width > 0)
                                    ? data.image_height / data.image_width
                                    : 0.75;
                            } catch (e) { colHeights[colIdx] += 0.75; }
                        });
                    }
                });
                colHeights.forEach((h, i) => formData.append(`col_heights[${i}]`, h));
            }
        }

        if (reset) this.updateURL(formData);

        const url = new URL(this.ajaxUrl, window.location.origin);
        for (const [key, value] of formData.entries()) {
            url.searchParams.append(key, value);
        }

        fetch(url.toString(), {
            method: 'GET',
            headers: {'X-Requested-With': 'XMLHttpRequest'}
        })
            .then(response => {
                if (!response.ok) throw new Error('Erreur serveur: ' + response.status);
                return response.text();
            })
            .then(responseText => {
                const jsonStartIndex = responseText.indexOf('{');
                const jsonEndIndex = responseText.lastIndexOf('}');
                if (jsonStartIndex === -1 || jsonEndIndex === -1) throw new Error('Aucun JSON trouvé');
                const jsonString = responseText.substring(jsonStartIndex, jsonEndIndex + 1);
                return JSON.parse(jsonString);
            })
            .then(data => {
                if (data.success) {
                    this.container.style.opacity = '1';

                    if (reset) {
                        this.container.innerHTML = data.html;
                        this.page = 1;
                        this.colCache = [];
                        if (this.observer) {
                            this.observer.disconnect();
                            this.initInfiniteScroll();
                        }
                        // Recalcul complet après un reset (filtres)
                        setTimeout(() => this.cacheColumnPositions(), 50);
                    } else {
                        const parser = new DOMParser();
                        const htmlDoc = parser.parseFromString(data.html, 'text/html');
                        const currentGrid = this.container.querySelector('#lab-grid');

                        if (currentGrid) {
                            htmlDoc.querySelectorAll('.lab-column').forEach(responseCol => {
                                const colIndex = responseCol.dataset.colIndex;
                                const targetCol = currentGrid.querySelector(`.lab-column[data-col-index="${colIndex}"]`);
                                if (!targetCol) return;
                                Array.from(responseCol.children).forEach(child => {
                                    const card = child.querySelector ? child.querySelector('.project_lab-card') : null;
                                    if (card) {
                                        const postId = card.dataset.id;
                                        if (postId && this.container.querySelector(`.project_lab-card[data-id="${postId}"]`)) return;
                                    }
                                    targetCol.appendChild(child.cloneNode(true));
                                });
                            });
                        }

                        const oldSentinel = document.getElementById('infinite-scroll-sentinel');
                        const oldLoader = document.getElementById('infinite-scroll-loader');
                        if (oldSentinel) oldSentinel.remove();
                        if (oldLoader) oldLoader.remove();

                        const newSentinel = htmlDoc.getElementById('infinite-scroll-sentinel');
                        const newLoader = htmlDoc.getElementById('infinite-scroll-loader');
                        
                        if (newSentinel) {
                            this.container.appendChild(newSentinel);
                            if (this.observer) this.observer.observe(newSentinel);
                        }
                        if (newLoader) this.container.appendChild(newLoader);

                        this.page++;
                        // On NE recalcule PAS cacheColumnPositions ici car le docTop ne change pas
                    }

                    if (data.pagination_info) {
                        this.maxPages = parseInt(data.pagination_info.max_num_pages);
                        const countEl = document.getElementById('results-count');
                        if (countEl && data.pagination_info.total_posts !== undefined) {
                            countEl.innerText = `${data.pagination_info.total_posts} projets`;
                        }
                    }

                    this.finished = (this.page >= this.maxPages);
                    const sentinel = document.getElementById('infinite-scroll-sentinel');
                    if (sentinel) sentinel.style.display = this.finished ? 'none' : 'block';

                    setTimeout(() => {
                        if (LazyLoadImages && typeof LazyLoadImages.init === 'function') LazyLoadImages.init();
                        LabModal.init();
                        LabModal.preloadAllImages();
                    }, 100);
                    setTimeout(() => this.cacheColumnPositions(), 50);
                }
            })
            .catch(error => {
                console.error('Erreur AJAX:', error);
                this.container.style.opacity = '1';
            })
            .finally(() => {
                this.loading = false;
                if (loader) loader.style.display = 'none';
            });
    }

    static updateURL(formData) {
        const urlParams = new URLSearchParams();
        for (const [key, value] of formData.entries()) {
            if (!['ajax', 'action', 'nonce', 'paged', 'max_pages'].includes(key) && value !== '') {
                urlParams.append(key, value);
            }
        }
        const queryString = urlParams.toString();
        const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
        window.history.pushState({path: newUrl}, '', newUrl);
    }
}
